import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function ProfileSetup() {
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loginid, setLoginid] = useState("");
  const [redirectTo, setRedirectTo] = useState("/pending");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const init = async () => {
      const storedLoginid = localStorage.getItem("currentDerivLogin");

      if (!storedLoginid) {
        navigate("/login", { replace: true });
        return;
      }

      setLoginid(storedLoginid);

      // Prefer state passed from LoginDeriv
      const state = location.state || {};
      if (state.redirectTo) setRedirectTo(state.redirectTo);
      if (state.prefillName) setName(state.prefillName);
      if (state.prefillEmail) setEmail(state.prefillEmail);

      // If state didn't have prefills, try loading from Firestore
      if (!state.prefillName || !state.prefillEmail) {
        try {
          const snap = await getDoc(doc(db, "deriv_users", storedLoginid));
          if (snap.exists()) {
            const data = snap.data();
            if (!state.prefillName && data.name) setName(data.name);
            if (!state.prefillEmail && data.email) setEmail(data.email);
            if (!state.redirectTo) {
              setRedirectTo(data.approved ? "/user" : "/pending");
            }
          }
        } catch (err) {
          console.warn("[ProfileSetup] Could not load existing data:", err);
        }
      }

      setLoading(false);
    };

    init();
  }, [navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName) { setError("Please enter your full name."); return; }
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSaving(true);
    try {
      await setDoc(
        doc(db, "deriv_users", loginid),
        {
          name: trimmedName,
          email: trimmedEmail,
          profileCompletedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      navigate(redirectTo, { replace: true });
    } catch (err) {
      console.error("[ProfileSetup] Save failed:", err);
      setError("Unable to save your details right now. Please try again.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#050b18] to-[#020617] flex items-center justify-center text-gray-400">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-4 border-cyan-700/40 border-t-cyan-400 animate-spin"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  const initials = name.trim()
    ? name.trim().split(" ").map((w) => w[0]?.toUpperCase()).slice(0, 2).join("")
    : loginid.slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#050b18] to-[#020617] text-white flex flex-col">

      {/* Ambient background */}
      <div className="fixed inset-0 opacity-15 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,#06b6d4_0%,transparent_55%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,#0ea5e9_0%,transparent_55%)]"></div>
      </div>

      {/* Nav */}
      <nav className="sticky top-0 bg-[#020617]/90 backdrop-blur-lg border-b border-cyan-900/40 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <span className="text-xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500">
            MatchesGuru
          </span>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-12 relative">
        <div className="w-full max-w-lg">

          {/* Avatar */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-3xl font-black text-white shadow-2xl shadow-cyan-600/40">
                {initials}
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-green-500 border-2 border-[#020617] flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Card */}
          <div className="bg-gradient-to-b from-[#0a1325] to-[#050814] rounded-3xl border border-cyan-800/50 p-8 sm:p-10 shadow-2xl shadow-cyan-950/40">

            <div className="text-center mb-8">
              <h1 className="text-3xl font-black text-white">Complete Your Profile</h1>
              <p className="text-gray-400 mt-2 text-sm leading-relaxed">
                You're connected via Deriv{loginid ? ` as ${loginid}` : ""}.<br />
                Add your name and email to finish setting up your account.
              </p>
            </div>

            {/* Deriv ID badge */}
            {loginid && (
              <div className="flex items-center gap-3 bg-cyan-900/20 border border-cyan-800/40 rounded-2xl px-4 py-3 mb-7">
                <div className="w-8 h-8 rounded-full bg-cyan-600/30 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-cyan-400 font-semibold uppercase tracking-wider">Deriv Login ID</p>
                  <p className="text-white font-mono text-sm truncate">{loginid}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name <span className="text-cyan-400">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  autoFocus
                  className="w-full px-5 py-3.5 bg-[#020617]/70 border border-cyan-900/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address <span className="text-cyan-400">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  className="w-full px-5 py-3.5 bg-[#020617]/70 border border-cyan-900/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-900/20 border border-red-700/40 text-red-300 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={saving}
                className={`w-full py-4 rounded-xl font-bold text-lg transition mt-2 ${
                  saving
                    ? "bg-cyan-700/50 cursor-not-allowed text-white/60"
                    : "bg-gradient-to-r from-cyan-600 via-blue-500 to-cyan-500 hover:from-cyan-500 hover:to-blue-400 shadow-lg shadow-cyan-900/40 text-white"
                }`}
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Saving...
                  </span>
                ) : (
                  "Save & Continue"
                )}
              </button>
            </form>

            {/* Note */}
            <p className="text-center text-xs text-gray-500 mt-6 leading-relaxed">
              This information is used to identify your account.<br />
              You won't need to enter it again.
            </p>
          </div>
        </div>
      </div>

      <footer className="py-6 text-center text-gray-600 text-xs border-t border-cyan-950/30">
        © 2026 MatchesGuru • Trading involves risk
      </footer>
    </div>
  );
}
